import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { ActionPlanView, SurveyActionPlanTopic } from '../../amorg-common';
import { getActionPlanFromLinkId } from '../../api/actionPlanClientApi';
import { Card } from '../../designSystem/Card';
import { colors } from '../../designSystem/colors/color';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { ListRow } from '../../designSystem/compositeComponents/ListRow';
import { StaticTextField } from '../../designSystem/formField/StaticTextField';
import { Icon, iconName } from '../../designSystem/icons/Icons';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../designSystem/Message';
import { P } from '../../designSystem/typography/Body';
import { H2, H4 } from '../../designSystem/typography/Heading';
import { ArticleVersionFields } from '../_components/articles/ArticleVersionFields';
import { PublicPageWrapper } from '../_pageWrappers/PublicPageWrapper';
import { SurveyTopicInfoPanel } from './components/SurveyTopicInfoPanel';
import { ReadOnlySegmentPanel } from '../workplaceassessments/[activityId]/_components/tabs/_components/ReadOnlySegmentPanel';
import { parseSiteScopedRouteParam } from '../../utils/parseSiteScopedRouteParam';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { PageHeader } from '../../designSystem/PageHeader';
import { ToolBarButton, ToolBarContainer } from '../../designSystem/compositeComponents/ButtonToolBar';
import PrintActionPlanView from './components/PrintActionPlanPage';

type PageState = {
    publicActionPlan: ActionPlanView | null;
    loading: boolean;
};
export default function ActionPlanPublicPage (): JSX.Element 
{
    const params = useParams<{ linkIdSiteId: string; }>();
    const linkRouteParams = parseSiteScopedRouteParam(params.linkIdSiteId);

    const [state, setState] = React.useState<PageState>({
        publicActionPlan: null,
        loading: true
    });

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const publicActionPlan = await getActionPlanFromLinkId(linkRouteParams.siteId, linkRouteParams.value);
            const newState = {
                publicActionPlan,
                loading: false
            };
            setState(newState);
        };
        getData();
    }, []);

    const segments = state.publicActionPlan?.type === 'WPA'
        ? state.publicActionPlan.customData.segmentFilter
        : [];

    const printRef = React.useRef(null);
    const printFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: state.publicActionPlan?.title || 'Delt handleplan'
    });

    return (
        <PublicPageWrapper siteId={linkRouteParams.siteId}>
            {state.publicActionPlan &&
                <PrintActionPlanView
                    actionPlan={state.publicActionPlan}
                    innerRef={printRef} />
            }

            <PageHeader
                noBottomMargin
                headerText="Delt handleplan"
                headerStyle="secondary-gray"
                buttons={
                    <ToolBarContainer>
                        <ToolBarButton
                            icon="interface_actions_print"
                            tooltip="Print"
                            variant="text"
                            onClick={printFn}
                        />
                    </ToolBarContainer>
                }
            />
            <SingleColumnLayout narrow>
                <>
                {state.loading &&
                    <LoadingPageOverlay loading={state.loading} />
                }

                <div style={{marginTop: '16px', marginBottom: '16px' }}>
                    {!state.loading && state.publicActionPlan === null &&
                        <MessageCard variant="error">Det valgte link er ugyldigt, eller ikke længere tilgængeligt. Kontakt din arbejdsmiljøgruppe.</MessageCard>
                    }

                    {!state.loading && state.publicActionPlan !== null &&
                        <Flex flexDirection='column' gap={32}>
                            <H2>{state.publicActionPlan?.title || ''}</H2>

                            <StaticTextField label="Beskrivelse"
                                staticText={state.publicActionPlan?.description || 'Ingen beskrivelse'} />

                            <StaticTextField label="Ansvarlig for handleplanen"
                                staticText={state.publicActionPlan?.owner?.fullName || 'Ingen valgt'} />
                            
                            {state.publicActionPlan?.type === 'WPA' && state.publicActionPlan.customData.segmentFilter.length > 0 &&
                                <div>
                                    <H2>Segmenter</H2>
                                    <ReadOnlySegmentPanel segmentLists={segments} lockedView='pills' />
                                </div>
                            }

                            {state.publicActionPlan?.type === 'Article' &&
                                <ArticleVersionFields {...state.publicActionPlan.articleData} 
                                    articleId={state.publicActionPlan.customData.articleId} 
                                    importance={state.publicActionPlan.articleData.importance}
                                    selectedCategoryTag={state.publicActionPlan.articleData.categoryTag}
                                    disableEdit />
                            }

                            <ActionPlanPublicView actionPlan={state.publicActionPlan} />
                        </Flex>
                    }
                </div>
                </>
            </SingleColumnLayout>
        </PublicPageWrapper>
    );
}

type ActionPlanPublicViewProps =
    {
        actionPlan: ActionPlanView;
    };
function ActionPlanPublicView (props: ActionPlanPublicViewProps): JSX.Element
{
    return (
        <>

            <H2>Handleplanens emner</H2>

            {props.actionPlan.topics.length === 0 &&
                <i>Ingen emner oprettet</i>
            }
            {props.actionPlan.topics.length > 0 &&
                <Flex flexDirection="column" gap={24}>
                    {props.actionPlan.topics.map(t =>
                    {
                        return (
                            <PublicActionPlanTopicView
                                key={t.id}
                                topic={t} />
                        );
                    })}
                </Flex>
            }
        </>
    );
}

type PublicActionPlanTopicViewProps =
    {
        topic: SurveyActionPlanTopic;
    };
function PublicActionPlanTopicView (props: PublicActionPlanTopicViewProps): JSX.Element 
{
    return (
        <Flex flexDirection="column" gap={2}>
            <ListRow content={<P>{props.topic.description}</P>} />
            <Card variant="outlined">
                <Flex flexDirection="column" gap={16}>
                    {props.topic.surveyTopic &&
                        <SurveyTopicInfoPanel surveyTopic={props.topic.surveyTopic} />
                    }
                    <div>
                        {props.topic.actionPoints.length === 0 &&
                            <>
                                <i>Ingen handlepunkter oprettet</i>
                            </>
                        }

                        {props.topic.actionPoints.length > 0 &&
                            <>
                                <H4>Handlepunkter</H4>
                                {props.topic.actionPoints.map((ap) =>
                                {
                                    let icon: { name: iconName, color: string; } = { name: 'clock', color: colors.icon.passive };
                                    if (ap.done)
                                        icon = { name: 'status_ok', color: colors.icon.success };
                                    else if (!ap.done && ap.deadline && new Date(ap.deadline) < new Date())
                                        icon = { name: 'status_error', color: colors.icon.error };

                                    return (
                                        <ListRow key={ap.id} styleVariant="outlined" level={1}
                                            content={
                                                <Flex justifyContent="space-between" gap={32} alignItems='center'>
                                                <Flex alignItems="center" gap={8} style={{ flexGrow: 1 }}>

                                                    <Icon name={icon.name} color={icon.color} />

                                                    <P>{ap.description}</P>
                                                </Flex>



                                                <Flex flexDirection="column" justifyContent="center" gap={0}>
                                                    <Flex alignItems='center' gap={8} style={{ width: '240px' }}>
                                                        <Icon name="interface_user_user" />
                                                        <div style={{ flexGrow: 1 }}>
                                                            { ap.assignedUser === null
                                                                ? <span style={{ color: colors.text.inactive }}>Ingen ejer</span>
                                                                : <span>{ap.assignedUser.fullName}</span>
                                                            }
                                                        </div>
                                                    </Flex>

                                                    <Flex alignItems='center' gap={8}>
                                                        <Icon name="calendar" />

                                                        <div style={{ width: '170px' }}>
                                                            {ap.deadline === null 
                                                                ? <span style={{ color: colors.text.inactive }}>Ingen deadline</span>
                                                                : <span>{ap.deadline.toLocaleDateString()}</span>
                                                            }
                                                        </div>
                                                    </Flex>

                                                </Flex>

                                                <Flex flexDirection="column" style={{width:'100px', flexGrow:0}}>
                                                    {ap.done &&
                                                        <i>Opgaven er gennemført</i>
                                                    }
                                                </Flex>

                                                </Flex>
                                            } />
                                    );
                                })}

                            </>
                        }
                    </div>
                </Flex>
            </Card >
        </Flex >
    );
}